import { useState, useEffect, useCallback } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import "firebase/compat/storage";

import UserData from "../data/UserData";

/** Max unprocessed videos loaded for the pending queue (keeps Firestore reads bounded). */
export const PENDING_VIDEOS_LIMIT = 100;

/**
 * Load org shared videos for the Media pending queue.
 * Does not subscribe to clients (unused for filtering) and does not live-listen
 * to the full video collection — one query per enable/refresh.
 */
const useSharedVideos = ({
  showAll = false,
  coachUID: _coachUID,
  organizationId,
  enabled = true,
  limit = PENDING_VIDEOS_LIMIT,
}) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const fetchVideos = useCallback(async () => {
    if (!enabled || !organizationId) {
      setVideos([]);
      setCount(0);
      setHasMore(false);
      setLoading(false);
      setIsRefreshing(false);
      return;
    }

    setIsRefreshing(true);

    try {
      const sharedVideosRef = firebase
        .firestore()
        .collection(`organizations/${organizationId}/sharedVideos`);

      let query;
      if (showAll) {
        query = sharedVideosRef.orderBy("createdOn", "desc").limit(limit);
      } else {
        query = sharedVideosRef
          .where("processed", "!=", true)
          .orderBy("processed", "desc")
          .orderBy("createdOn", "desc")
          .limit(limit);
      }

      const sharedVideoDocs = await query.get();
      const videosData = sharedVideoDocs.docs.map((doc) => doc.data());
      const videoCount = videosData.length;
      setHasMore(videoCount >= limit);

      const groupedVideos = videosData.reduce((acc, video) => {
        if (!video?.createdOn?.toDate) return acc;
        const videoDate = video.createdOn.toDate();
        const dateKey = videoDate.toDateString();
        const hourKey = videoDate.getHours();
        const clientKey = video.clientId;

        if (!acc[dateKey]) acc[dateKey] = {};
        if (!acc[dateKey][hourKey]) acc[dateKey][hourKey] = {};
        if (!acc[dateKey][hourKey][clientKey]) {
          acc[dateKey][hourKey][clientKey] = [];
        }
        acc[dateKey][hourKey][clientKey].push(video);
        return acc;
      }, {});

      const flattenedVideos = Object.keys(groupedVideos).map((dateKey) => {
        const hours = Object.keys(groupedVideos[dateKey]).map((hourKey) => {
          const clients = Object.keys(groupedVideos[dateKey][hourKey]).map(
            (clientKey) => ({
              clientUID: clientKey,
              videos: groupedVideos[dateKey][hourKey][clientKey],
            }),
          );
          return { hour: hourKey, clients };
        });
        return { date: dateKey, hours };
      });

      flattenedVideos.sort((a, b) => new Date(a.date) - new Date(b.date));

      setCount(videoCount);
      setVideos(flattenedVideos);
    } catch (error) {
      console.error("useSharedVideos fetch failed:", error);
      setVideos([]);
      setCount(0);
      setHasMore(false);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [enabled, organizationId, showAll, limit]);

  useEffect(() => {
    setLoading(true);
    fetchVideos();
  }, [fetchVideos, lastUpdated]);

  const deleteSharedVideo = async ({ id, uid }) => {
    if (!id || !uid || !organizationId) return;

    const ref = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("sharedVideos")
      .doc(id);

    const sharedFileRef = firebase
      .storage()
      .ref(`/${organizationId}/shared_videos/${uid}/${id}.mp4`);
    await sharedFileRef.delete();
    await ref.delete();
  };

  const setVideoProcessedState = async ({
    id,
    feedback,
    uid,
    coachUID,
    processed = true,
  }) => {
    if (!uid || !id || !coachUID) return;

    const coachUser = await UserData.getUserData(coachUID);
    if (!coachUser) return;

    const ref = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("sharedVideos")
      .doc(id);

    await ref.set(
      {
        processed,
        processedOn: processed ? new Date() : null,
        feedback: feedback || null,
        processedBy: processed
          ? {
              uid: coachUser.uid,
              firstName: coachUser.firstName,
              lastName: coachUser.lastName,
            }
          : null,
      },
      { merge: true },
    );
  };

  return {
    videos,
    loading,
    /** @deprecated Clients are no longer loaded; always false. */
    loadingClients: false,
    isRefreshing,
    count,
    hasMore,
    deleteSharedVideo,
    setVideoProcessedState,
    setLastUpdated,
  };
};

export default useSharedVideos;
